import { FileUploader } from '@brighterion/br-ui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import useBulkListEntry from 'Hooks/query-hooks/listEntry/useBulkListEntry';
import { getISOStringFromString } from '@brighterion/utils/date-timezone';
import { ListEntryBulkObject } from '@brighterion/types/listEntry';
import {
    deleteFile,
    POSSIBLE_STATUS,
    toggleConfirmModal,
    updateCurrentChunkNumber,
    updateStatus,
    updateValidFile,
    updateValidFileDetails,
    useUploaderContext,
    useUploaderDispatchContext
} from '../Uploader/context';
import useListTypeBulkUploaderColumns from '../../hooks/useListTypeBulkUploaderColumns';
import styles from './GenericListFileUploader.css';
import useBulkListChunkingComplete from '../../hooks/useBulkListChunkingCompleteAddSuccessModal';
import useCsvWorker from './csv-worker';
import { processFile, validateHeaders } from './csv-worker/csv-worker.slice';
import useBulkListServerError from '../../hooks/useBulkListServerError';

const GenericListFileUploader = () => {
    const { t } = useTranslation();
    const {
        listType,
        listId,
        status,
        currentChunkNumber,
        fileDetails,
        file,
        maxFileSize,
        merchantSpecific,
        chunkSize,
        customListType
    } = useUploaderContext();
    const isChunking = status === POSSIBLE_STATUS.chunking;
    const mutation = useBulkListEntry({ listId });
    const successfulBulk = useBulkListChunkingComplete();
    const errorBulking = useBulkListServerError();
    const dispatch = useUploaderDispatchContext();
    const userColumns = useListTypeBulkUploaderColumns(listType, merchantSpecific, customListType);
    const [workerId, setWorkerId] = React.useState(0);
    const [stateWorkSheetJson, setStateWorkSheetJson] = React.useState<any[]>([]);
    const [errorMsg, setErrorMsg] = React.useState('');

    const worker = useCsvWorker({
        workerId,
        onValidateHeaders: React.useCallback(
            (val) => {
                if (val) {
                    setErrorMsg(val);
                    dispatch(updateStatus(POSSIBLE_STATUS.error));
                } else {
                    setErrorMsg('');
                    dispatch(updateStatus(POSSIBLE_STATUS.validFile));
                }
            },
            [dispatch]
        ),
        onSuccess: React.useCallback(
            (payload) => {
                dispatch(updateValidFileDetails({ ...payload.fileDetails }));
                dispatch(updateStatus(POSSIBLE_STATUS.processingFileComplete));
                dispatch(toggleConfirmModal({ value: true, changeStatus: false }));
                setStateWorkSheetJson(payload.workSheet);
            },
            [dispatch]
        )
    });

    React.useEffect(() => {
        const setUpReader = (action: typeof validateHeaders | typeof processFile) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                const data = e.target?.result;

                if (data) {
                    worker?.postMessage(
                        action({
                            userColumns,
                            file: new Uint8Array(data as Buffer),
                            chunkSize
                        })
                    );
                }
            };
            if (file) {
                reader.readAsArrayBuffer(file);
            }
        };

        switch (status) {
            case POSSIBLE_STATUS.chunking:
                if (currentChunkNumber === fileDetails.totalChunks) {
                    dispatch(updateStatus(POSSIBLE_STATUS.chunkingComplete));
                } else {
                    const chunkedCSV = stateWorkSheetJson[currentChunkNumber];
                    chunkedCSV?.forEach((row: ListEntryBulkObject) => {
                        if (row.startTime) {
                            row.startTime = getISOStringFromString(row.startTime);
                        }
                        if (row.endTime) {
                            row.endTime = getISOStringFromString(row.endTime);
                        }
                    });

                    mutation.mutate(chunkedCSV, {
                        onSuccess: () => {
                            dispatch(updateCurrentChunkNumber(currentChunkNumber + 1));
                        },
                        onError: errorBulking
                    });
                }
                break;
            case POSSIBLE_STATUS.validatingHeaders: {
                setUpReader(validateHeaders);
                break;
            }
            case POSSIBLE_STATUS.chunkingComplete: {
                dispatch(updateStatus(POSSIBLE_STATUS.chunkingComplete));
                successfulBulk();
                break;
            }
            case POSSIBLE_STATUS.processingFile: {
                setUpReader(processFile);
                break;
            }
            default:
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, currentChunkNumber, file, worker]);

    // This should never happen but handle the case we don't have a defined column config for bulk
    if (!userColumns.length) {
        return <h2 className={styles.notDefined}>Bulk uploader config missing</h2>;
    }

    return (
        <div className={clsx(styles.genericListFileUploaderContainer, isChunking && styles.chunking)}>
            <FileUploader
                dataTestId={`${listType}-bulk-uploader`}
                maxSize={maxFileSize}
                maxTotalSize={maxFileSize}
                disabled={status === POSSIBLE_STATUS.validatingHeaders}
                name='file'
                accept='.csv'
                onChangeProp={(files) => {
                    const [aFile] = files;
                    if (aFile) {
                        dispatch(updateValidFile(aFile));
                    }
                }}
                errorMsg={errorMsg}
                onDelete={() => {
                    worker?.terminate();
                    setWorkerId((c) => c + 1);
                    setErrorMsg('');
                    setStateWorkSheetJson([]);
                    dispatch(deleteFile());
                }}
                successMsg={
                    status === POSSIBLE_STATUS.validatingHeaders
                        ? t('Validating headers')
                        : t('File is ready to preview and upload')
                }
                placeholder={
                    <>
                        {`${t('Drag and drop file here or')} `}
                        <span className={styles.browse}>{t('Browse')}</span>
                    </>
                }
            />
        </div>
    );
};

export default GenericListFileUploader;
